import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Card,
  CardContent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SchoolIcon from "@mui/icons-material/School";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StarIcon from "@mui/icons-material/Star";
import LayersIcon from "@mui/icons-material/Layers";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import DescriptionIcon from "@mui/icons-material/Description";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalculateIcon from "@mui/icons-material/Calculate";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useNavigate, useLocation } from "react-router-dom";

const THEME = {
  bg: "#FBF8F1",
  surface: "#FFFFFF",
  surfaceTint: "#EAF2FE",
  surfaceTint2: "#E4EEFD",
  lavender: "#ECEAFB",
  mint: "#E3F5EA",
  ink: "#152A4A",
  navy: "#152A4A",
  navyDeep: "#0D1B32",
  navyMid: "#1F4E8C",
  textMuted: "#5C6B85",
  textFaint: "#94A2B8",
  line: "rgba(21, 42, 74, 0.12)",
  lineStrong: "rgba(21, 42, 74, 0.24)",
  blue: "#2F6FE0",
  blueDeep: "#1E4FB0",
  blueBlob: "#CFE3FB",
  blueBlobSoft: "#E3EFFD",
  gold: "#F0A93C",
  goldDeep: "#C97F14",
  mintDeep: "#2E9E63",
  coral: "#D96455",
  serif: "'Source Serif 4', Georgia, serif",
  sans: "'Nunito', 'Inter', 'Segoe UI', system-ui, sans-serif",
  display: "'Baloo 2', 'Nunito', sans-serif",
  hand: "'Caveat', cursive",
  mono: "'Space Grotesk', 'Inter', system-ui, sans-serif",
};

const gradeColor = (grade) => {
  switch (grade) {
    case "O":
      return THEME.mintDeep;
    case "A+":
      return THEME.blue;
    case "A":
      return THEME.blueDeep;
    case "B+":
      return THEME.gold;
    case "B":
      return THEME.goldDeep;
    case "C":
      return "#B36B2E";
    default:
      return THEME.coral;
  }
};

const COURSE_TYPES = [
  { value: "BE", label: "BE / B.Tech (Engineering)", semesters: 8 },
  { value: "ME", label: "ME / M.Tech (Engineering PG)", semesters: 4 },
  { value: "ARTS", label: "Arts / Science (UG)", semesters: 6 },
];

const DEPARTMENTS_BY_DEGREE = {
  BE: [
    "Computer Science Engineering (CSE)",
    "Electronics & Communication Engineering (ECE)",
    "Electrical & Electronics Engineering (EEE)",
    "Mechanical Engineering",
    "Civil Engineering",
    "Information Technology (IT)",
    "Other",
  ],
  ME: [
    "Computer Science Engineering (CSE)",
    "VLSI Design",
    "Structural Engineering",
    "Power Systems Engineering",
    "Other",
  ],
  ARTS: [
    "B.Sc Computer Science",
    "B.Sc Physics",
    "B.Sc Mathematics",
    "B.Com",
    "B.A English",
    "Other",
  ],
};

const SUBJECT_TYPES = ["Theory", "Theory + Lab", "Lab", "Seminar", "Project"];

const getGradeFromMarks = (marks) => {
  const m = parseFloat(marks);
  if (isNaN(m)) return { grade: "O", point: 10 };
  if (m >= 91) return { grade: "O", point: 10 };
  if (m >= 81) return { grade: "A+", point: 9 };
  if (m >= 71) return { grade: "A", point: 8 };
  if (m >= 61) return { grade: "B+", point: 7 };
  if (m >= 56) return { grade: "B", point: 6 };
  if (m >= 50) return { grade: "C", point: 5 };
  return { grade: "U/RA", point: 0 };
};

const GRADE_POINTS = [
  { grade: "O", point: 10, range: "91-100" },
  { grade: "A+", point: 9, range: "81-90" },
  { grade: "A", point: 8, range: "71-80" },
  { grade: "B+", point: 7, range: "61-70" },
  { grade: "B", point: 6, range: "56-60" },
  { grade: "C", point: 5, range: "50-55" },
  { grade: "U/RA", point: 0, range: "0-49" },
];

const emptySubject = () => ({
  id: Date.now() + Math.random(),
  code: "",
  name: "",
  type: "Theory",
  marks: "",
  grade: "O",
  credit: 4,
});

const cgpaToPercentage = (cgpaValue) => {
  const value = parseFloat(cgpaValue) * 10 - 7.5;
  if (isNaN(value)) return "0.00";
  return Math.min(100, Math.max(0, value)).toFixed(2);
};

const getResultLabel = (value) => {
  const v = parseFloat(value);
  if (v >= 9) return "Outstanding";
  if (v >= 8) return "Excellent";
  if (v >= 7) return "Very Good";
  if (v >= 6) return "Good";
  if (v >= 5) return "Average";
  return "Needs Improvement";
};

const Sparkle = ({ sx }) => (
  <Box
    aria-hidden="true"
    sx={{
      position: "absolute",
      fontFamily: THEME.hand,
      color: THEME.navyMid,
      fontSize: "1.4rem",
      lineHeight: 1,
      ...sx,
    }}
  >
    ✧
  </Box>
);

const GraduationIllustration = () => (
  <Box sx={{ position: "relative", width: 190, height: 170 }}>
    <Sparkle sx={{ top: -6, left: 6, fontSize: "1.1rem", opacity: 0.7 }} />
    <Sparkle sx={{ top: 10, right: 6, fontSize: "0.9rem", opacity: 0.5 }} />
    <svg viewBox="0 0 220 200" width="100%" height="100%">
      <rect x="35" y="150" width="150" height="26" rx="4" fill="#E8A93B" />
      <rect x="45" y="126" width="130" height="26" rx="4" fill="#2F6FE0" />
      <rect x="50" y="118" width="120" height="10" rx="3" fill="#FFFFFF" opacity="0.85" />
      <g transform="translate(38,40)">
        <polygon points="72,0 144,32 72,64 0,32" fill="#152A4A" />
        <rect x="52" y="32" width="40" height="28" fill="#1F4E8C" />
        <circle cx="72" cy="60" r="6" fill="#F0A93C" />
        <line x1="130" y1="30" x2="150" y2="72" stroke="#F0A93C" strokeWidth="3" />
        <circle cx="150" cy="76" r="5" fill="#F0A93C" />
      </g>
    </svg>
  </Box>
);

const PencilCupIllustration = () => (
  <svg viewBox="0 0 140 140" width="120" height="120" aria-hidden="true">
    <rect x="18" y="70" width="80" height="18" rx="3" fill="#E8A93B" />
    <rect x="24" y="52" width="68" height="18" rx="3" fill="#2F6FE0" />
    <rect x="30" y="30" width="46" height="45" rx="6" fill="#F4F8FE" stroke="#152A4A" strokeWidth="2" />
    <line x1="38" y1="8" x2="45" y2="32" stroke="#F0A93C" strokeWidth="5" strokeLinecap="round" />
    <line x1="50" y1="4" x2="53" y2="32" stroke="#2F6FE0" strokeWidth="5" strokeLinecap="round" />
    <line x1="62" y1="10" x2="58" y2="32" stroke="#D96455" strokeWidth="5" strokeLinecap="round" />
  </svg>
);

const StepperField = ({ label, value, onChange, step = 1, min = 0, max, tooltip, decimals = 0, icon }) => {
  const clamp = (v) => {
    let val = parseFloat(v);
    if (isNaN(val)) val = min;
    if (min !== undefined) val = Math.max(min, val);
    if (max !== undefined) val = Math.min(max, val);
    return decimals > 0 ? parseFloat(val.toFixed(decimals)) : Math.round(val);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
        {icon}
        <Typography variant="body2" sx={{ color: THEME.textMuted, fontWeight: 700, fontFamily: THEME.sans }}>
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <HelpOutlineIcon sx={{ fontSize: 15, color: THEME.textFaint, cursor: "help" }} />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: THEME.surface,
          border: `1.5px solid ${THEME.line}`,
          borderRadius: "999px",
          overflow: "hidden",
          transition: "border-color 0.15s ease",
          "&:focus-within": { borderColor: THEME.blue },
        }}
      >
        <TextField
          variant="standard"
          value={value}
          onChange={(e) => onChange(clamp(e.target.value))}
          InputProps={{ disableUnderline: true }}
          sx={{
            flex: 1,
            px: 2.5,
            py: 0.5,
            "& input": { fontSize: "0.95rem", color: THEME.ink, fontFamily: THEME.mono, fontWeight: 600 },
          }}
        />
        <IconButton
          size="small"
          onClick={() => onChange(clamp(value - step))}
          sx={{ borderRadius: 0, px: 1.4, color: THEME.textMuted, "&:hover": { color: THEME.blue, backgroundColor: THEME.surfaceTint } }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ borderColor: THEME.line }} />
        <IconButton
          size="small"
          onClick={() => onChange(clamp(value + step))}
          sx={{ borderRadius: 0, px: 1.4, color: THEME.textMuted, "&:hover": { color: THEME.blue, backgroundColor: THEME.surfaceTint } }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const IconBadge = ({ children, bg = THEME.surfaceTint, color = THEME.blue }) => (
  <Box
    sx={{
      width: 34,
      height: 34,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: bg,
      color,
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const CgpaPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navDegree = location.state?.degree;
  const navDepartment = location.state?.department;
  const navTotalSemesters = location.state?.totalSemesters;

  const [courseType, setCourseType] = useState(navDegree || "BE");
  const [department, setDepartment] = useState(navDepartment || "");
  const [totalSemesters, setTotalSemesters] = useState(
    navTotalSemesters || (COURSE_TYPES.find((c) => c.value === (navDegree || "BE"))?.semesters ?? 8)
  );

  const [openDegreeDialog, setOpenDegreeDialog] = useState(!(navDegree && navDepartment));
  const [dialogDegree, setDialogDegree] = useState(navDegree || "");
  const [dialogDept, setDialogDept] = useState(navDepartment || "");
  const [dialogDeptOther, setDialogDeptOther] = useState("");

  const handleDialogDegreeChange = (value) => {
    setDialogDegree(value);
    setDialogDept("");
    setDialogDeptOther("");
  };

  const handleDialogContinue = () => {
    const degreeInfo = COURSE_TYPES.find((d) => d.value === dialogDegree);
    const finalDept = dialogDept === "Other" ? dialogDeptOther.trim() : dialogDept;

    setCourseType(dialogDegree);
    setDepartment(finalDept);
    setTotalSemesters(degreeInfo ? degreeInfo.semesters : 8);
    setActiveSemester(1);
    setFinalResult(null);
    setOpenDegreeDialog(false);
  };

  const handleDialogCancel = () => {
    navigate("/");
  };

  const [previousCgpa, setPreviousCgpa] = useState(0);
  const [previousCredit, setPreviousCredit] = useState(0);
  const [semesterSubjects, setSemesterSubjects] = useState({});
  const [semesterResults, setSemesterResults] = useState({});
  const [activeSemester, setActiveSemester] = useState(1);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState("");

  const handleCourseTypeChange = (value) => {
    setCourseType(value);
    const degree = COURSE_TYPES.find((c) => c.value === value);
    const nextTotal = degree ? degree.semesters : 8;
    setTotalSemesters(nextTotal);
    setActiveSemester(1);
    setFinalResult(null);
  };

  useEffect(() => {
    const PAGE_TITLE = "CGPA Calculator Online Free – Semester & Cumulative CGPA to Percentage";
    const PAGE_DESCRIPTION =
      "Free online CGPA calculator for Anna University, BE, B.Tech, ME, M.Tech and Arts & Science students. Calculate semester GPA, cumulative CGPA, and convert CGPA to percentage instantly.";
    const PAGE_KEYWORDS =
      "cgpa calculator, cgpa to percentage calculator, anna university cgpa calculator, semester gpa calculator, cumulative cgpa calculator, cgpa calculator online free, be cgpa calculator, engineering cgpa calculator";
    const CANONICAL_URL = typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";

    document.title = PAGE_TITLE;

    const setMetaTag = (attr, key, content) => {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMetaTag("name", "description", PAGE_DESCRIPTION);
    setMetaTag("name", "keywords", PAGE_KEYWORDS);
    setMetaTag("property", "og:title", PAGE_TITLE);
    setMetaTag("property", "og:description", PAGE_DESCRIPTION);
    setMetaTag("property", "og:type", "website");
    setMetaTag("name", "twitter:card", "summary");
    setMetaTag("name", "twitter:title", PAGE_TITLE);
    setMetaTag("name", "twitter:description", PAGE_DESCRIPTION);

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    if (CANONICAL_URL) canonicalLink.setAttribute("href", CANONICAL_URL);

    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          name: "CGPA Calculator",
          applicationCategory: "EducationApplication",
          operatingSystem: "Any",
          offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
          description: PAGE_DESCRIPTION,
        },
        {
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "How is CGPA calculated?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "CGPA is calculated by multiplying each subject's credit by its grade point, adding these up across all subjects and semesters, then dividing by the total credits.",
              },
            },
            {
              "@type": "Question",
              name: "How do I convert CGPA to percentage?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "For Anna University and most autonomous colleges following the 10-point scale, Percentage = (CGPA × 10) − 7.5.",
              },
            },
            {
              "@type": "Question",
              name: "What is the difference between GPA and CGPA?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "GPA (Grade Point Average) is your performance for a single semester, while CGPA (Cumulative Grade Point Average) is your average performance across all completed semesters.",
              },
            },
          ],
        },
      ],
    };

    let scriptTag = document.getElementById("cgpa-structured-data");
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = "cgpa-structured-data";
      scriptTag.type = "application/ld+json";
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);
  }, []);

  useEffect(() => {
    if (document.getElementById("cgpa-theme-fonts")) return;
    const link = document.createElement("link");
    link.id = "cgpa-theme-fonts";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Caveat:wght@600;700&family=Nunito:wght@400;600;700;800&family=Space+Grotesk:wght@500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  const getSubjects = (sem) => semesterSubjects[sem] || [];

  const addSubject = (sem) => {
    setSemesterSubjects((prev) => ({
      ...prev,
      [sem]: [...(prev[sem] || []), emptySubject()],
    }));
  };

  const removeSubject = (sem, id) => {
    setSemesterSubjects((prev) => ({
      ...prev,
      [sem]: (prev[sem] || []).filter((s) => s.id !== id),
    }));
  };

  const updateSubject = (sem, id, field, value) => {
    setSemesterSubjects((prev) => ({
      ...prev,
      [sem]: (prev[sem] || []).map((s) => {
        if (s.id !== id) return s;
        const updated = { ...s, [field]: value };
        if (field === "marks") updated.grade = getGradeFromMarks(value).grade;
        return updated;
      }),
    }));
  };

  const calculateSemesterGPA = (sem) => {
    setError("");
    const entries = getSubjects(sem);
    if (entries.length === 0) {
      setError(`Add at least one subject to Semester ${sem} first.`);
      return;
    }

    let credits = 0;
    let gradePoints = 0;
    for (const entry of entries) {
      const credit = parseFloat(entry.credit);
      const gradeObj = GRADE_POINTS.find((g) => g.grade === entry.grade);
      if (isNaN(credit) || credit <= 0) {
        setError(`Enter a valid credit value for every subject in Semester ${sem}.`);
        return;
      }
      if (!gradeObj) {
        setError(`Select a valid grade for every subject in Semester ${sem}.`);
        return;
      }
      credits += credit;
      gradePoints += credit * gradeObj.point;
    }
    if (credits === 0) {
      setError(`Total credits for Semester ${sem} cannot be zero.`);
      return;
    }

    const gpa = gradePoints / credits;
    setSemesterResults((prev) => ({
      ...prev,
      [sem]: { gpa: gpa.toFixed(2), pct: cgpaToPercentage(gpa), credits, gradePoints },
    }));
  };

  const calculateFinalCgpa = () => {
    setError("");
    let enteredCredits = 0;
    let enteredGradePoints = 0;
    let semestersCounted = 0;

    for (let sem = 1; sem <= totalSemesters; sem++) {
      const entries = getSubjects(sem);
      if (entries.length === 0) continue;

      for (const entry of entries) {
        const credit = parseFloat(entry.credit);
        const gradeObj = GRADE_POINTS.find((g) => g.grade === entry.grade);
        if (isNaN(credit) || credit <= 0 || !gradeObj) continue;
        enteredCredits += credit;
        enteredGradePoints += credit * gradeObj.point;
      }
      semestersCounted += 1;
    }

    const prevCgpaNum = parseFloat(previousCgpa) || 0;
    const prevCreditNum = parseFloat(previousCredit) || 0;

    if (enteredCredits === 0 && prevCreditNum === 0) {
      setError(
        "Add subjects to at least one semester, or enter your previous CGPA and credits, before generating the final CGPA."
      );
      setFinalResult(null);
      return;
    }

    const semesterGpa = enteredCredits > 0 ? enteredGradePoints / enteredCredits : 0;
    const overallCredits = prevCreditNum + enteredCredits;
    const overallGradePoints = prevCgpaNum * prevCreditNum + enteredGradePoints;
    const overallCgpa = overallCredits > 0 ? overallGradePoints / overallCredits : semesterGpa;

    setFinalResult({
      previousCgpa: prevCreditNum > 0 ? prevCgpaNum.toFixed(2) : null,
      previousPct: prevCreditNum > 0 ? cgpaToPercentage(prevCgpaNum) : null,
      previousCredit: prevCreditNum,
      semesterGpa: enteredCredits > 0 ? semesterGpa.toFixed(2) : null,
      semesterPct: enteredCredits > 0 ? cgpaToPercentage(semesterGpa) : null,
      semesterCredits: enteredCredits,
      cgpa: overallCgpa.toFixed(2),
      pct: cgpaToPercentage(overallCgpa),
      totalCredits: overallCredits,
      semestersCounted,
    });
  };

  const resetAll = () => {
    setSemesterSubjects({});
    setSemesterResults({});
    setActiveSemester(1);
    setPreviousCgpa("");
    setPreviousCredit("");
    setFinalResult(null);
    setError("");
  };

  const selectSx = {
    backgroundColor: THEME.surface,
    borderRadius: "14px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: THEME.line },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: THEME.lineStrong },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: THEME.blue, borderWidth: "1.5px" },
    "& .MuiSelect-select": { color: THEME.ink, fontFamily: THEME.sans, fontWeight: 700 },
    "& .MuiSvgIcon-root": { color: THEME.textMuted },
  };

  const activeSubjects = getSubjects(activeSemester);
  const activeResult = semesterResults[activeSemester];
  const degreeLabel = COURSE_TYPES.find((c) => c.value === courseType)?.label || courseType;

  return (
    <Box
      sx={{
        backgroundColor: THEME.bg,
        minHeight: "100vh",
        py: { xs: 3, md: 5 },
        fontFamily: THEME.sans,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: -80,
          right: -80,
          width: 320,
          height: 320,
          borderRadius: "50%",
          background: THEME.blueBlobSoft,
          zIndex: 0,
        }}
      />
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          bottom: -100,
          right: -100,
          width: 340,
          height: 340,
          borderRadius: "50%",
          background: THEME.blueBlobSoft,
          zIndex: 0,
        }}
      />

      <Dialog
        open={openDegreeDialog}
        onClose={handleDialogCancel}
        fullWidth
        maxWidth="xs"
        aria-labelledby="cgpa-dialog-title"
        PaperProps={{ sx: { backgroundColor: THEME.surface, border: `1.5px solid ${THEME.lineStrong}`, borderRadius: "18px" } }}
      >
        <DialogTitle id="cgpa-dialog-title" sx={{ fontFamily: THEME.display, fontWeight: 700, color: THEME.ink }}>
          Choose your degree
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: "0.875rem", color: THEME.textMuted }}>
            Select your degree and department to open the matching CGPA calculator.
          </DialogContentText>

          <FormControl fullWidth sx={{ mb: 2, ...selectSx }}>
            <InputLabel id="cgpa-degree-label" sx={{ color: THEME.textMuted, "&.Mui-focused": { color: THEME.blue } }}>
              Degree
            </InputLabel>
            <Select
              labelId="cgpa-degree-label"
              label="Degree"
              value={dialogDegree}
              onChange={(e) => handleDialogDegreeChange(e.target.value)}
              MenuProps={{ PaperProps: { sx: { backgroundColor: THEME.surface } } }}
            >
              {COURSE_TYPES.map((d) => (
                <MenuItem key={d.value} value={d.value} sx={{ color: THEME.ink, fontFamily: THEME.sans }}>
                  {d.label} — {d.semesters} sem
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!dialogDegree} sx={{ mb: dialogDept === "Other" ? 2 : 0, ...selectSx }}>
            <InputLabel id="cgpa-dept-label" sx={{ color: THEME.textMuted, "&.Mui-focused": { color: THEME.blue } }}>
              Department
            </InputLabel>
            <Select
              labelId="cgpa-dept-label"
              label="Department"
              value={dialogDept}
              onChange={(e) => setDialogDept(e.target.value)}
              MenuProps={{ PaperProps: { sx: { backgroundColor: THEME.surface } } }}
            >
              {(DEPARTMENTS_BY_DEGREE[dialogDegree] || []).map((dept) => (
                <MenuItem key={dept} value={dept} sx={{ color: THEME.ink, fontFamily: THEME.sans }}>
                  {dept}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {dialogDept === "Other" && (
            <TextField
              fullWidth
              label="Enter your department"
              value={dialogDeptOther}
              onChange={(e) => setDialogDeptOther(e.target.value)}
              sx={{
                ...selectSx,
                "& .MuiInputLabel-root": { color: THEME.textMuted },
                "& .MuiInputLabel-root.Mui-focused": { color: THEME.blue },
                "& .MuiOutlinedInput-input": { color: THEME.ink, fontFamily: THEME.sans },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel} sx={{ color: THEME.textMuted, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!dialogDegree || !dialogDept || (dialogDept === "Other" && !dialogDeptOther.trim())}
            onClick={handleDialogContinue}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "999px",
              color: "#FFFFFF",
              background: `linear-gradient(135deg, ${THEME.navyMid}, ${THEME.navy})`,
              "&:hover": { background: THEME.navy },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Button
          startIcon={<ArrowBackIcon sx={{ color: "#FFFFFF" }} />}
          onClick={() => navigate("/")}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontFamily: THEME.sans,
            color: "#FFFFFF",
            backgroundColor: THEME.navy,
            borderRadius: "999px",
            px: 2.5,
            py: 0.7,
            mb: 3,
            "&:hover": { backgroundColor: THEME.navyDeep },
          }}
        >
          Back
        </Button>

        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, mb: 1 }}>
              <SchoolIcon sx={{ color: THEME.navy, fontSize: 34, mt: 0.5 }} />
              <Typography
                component="h1"
                sx={{
                  fontFamily: THEME.display,
                  fontWeight: 700,
                  fontSize: { xs: "1.9rem", sm: "2.3rem" },
                  color: THEME.navy,
                  lineHeight: 1.15,
                }}
              >
                CGPA Calculator
                <br />
                Semester &amp; Cumulative
                <br />
                CGPA to Percentage
              </Typography>
            </Box>

            {department && (
              <Chip
                label={`${degreeLabel} - ${department}`}
                sx={{
                  backgroundColor: THEME.navy,
                  color: "#FFFFFF",
                  fontFamily: THEME.sans,
                  fontWeight: 700,
                  mb: 1.5,
                  borderRadius: "8px",
                  px: 0.5,
                }}
              />
            )}

            <Typography variant="body2" sx={{ color: THEME.textMuted, maxWidth: 460, lineHeight: 1.7 }}>
              Enter subjects semester by semester, calculate each semester's GPA, then generate
              your overall CGPA across the program.
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4} sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "center" }}>
            <Box sx={{ position: "relative", textAlign: "center" }}>
              <Typography sx={{ fontFamily: THEME.hand, fontSize: "1.4rem", color: THEME.navyMid, mb: -1 }}>
                Dream Plan Achieve
              </Typography>
              <GraduationIllustration />
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            position: "relative",
            textAlign: "center",
            mb: 3,
            py: 3,
            px: 2,
            backgroundColor: THEME.surfaceTint,
            borderRadius: "16px",
          }}
        >
          <Sparkle sx={{ top: 10, left: "8%" }} />
          <Sparkle sx={{ bottom: 10, right: "8%" }} />
          <Typography
            sx={{
              fontFamily: THEME.serif,
              fontStyle: "italic",
              fontSize: { xs: "1.1rem", sm: "1.35rem" },
              color: THEME.navy,
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            CGPA{" "}
            <Box component="span" sx={{ fontStyle: "normal", fontSize: "1.4rem", color: THEME.navy }}>
              =
            </Box>
            <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
              <Box component="span" sx={{ pb: "2px" }}>
                Σ (grade<sub>i</sub> × credit<sub>i</sub>)
              </Box>
              <Box component="span" sx={{ width: "100%", borderTop: `1.5px solid ${THEME.navy}`, my: "2px" }} />
              <Box component="span" sx={{ pt: "2px" }}>
                Σ credit<sub>i</sub>
              </Box>
            </Box>
          </Typography>
          <Typography sx={{ fontFamily: THEME.serif, fontStyle: "italic", fontSize: { xs: "0.85rem", sm: "0.95rem" }, color: THEME.navyMid, mt: 1 }}>
            Percentage = (CGPA × 10) − 7.5
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ backgroundColor: THEME.surface, borderRadius: "14px", border: `1px solid ${THEME.line}`, p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconBadge>
                <SchoolIcon fontSize="small" />
              </IconBadge>
              <TextField
                select
                fullWidth
                variant="standard"
                label="Degree"
                value={courseType}
                onChange={(e) => handleCourseTypeChange(e.target.value)}
                InputProps={{ disableUnderline: true }}
                sx={{ "& .MuiInputLabel-root": { color: THEME.textMuted, fontSize: "0.75rem" }, "& .MuiSelect-select": { color: THEME.ink, fontWeight: 700, fontFamily: THEME.sans } }}
              >
                {COURSE_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label} — {option.semesters} sem
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ backgroundColor: THEME.surface, borderRadius: "14px", border: `1px solid ${THEME.line}`, p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconBadge>
                <AccountBalanceIcon fontSize="small" />
              </IconBadge>
              <TextField
                fullWidth
                variant="standard"
                label="Department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g. Computer Science Engineering"
                InputProps={{ disableUnderline: true }}
                sx={{ "& .MuiInputLabel-root": { color: THEME.textMuted, fontSize: "0.75rem" }, "& .MuiInputBase-input": { color: THEME.ink, fontWeight: 700, fontFamily: THEME.sans } }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ position: "relative", backgroundColor: THEME.lavender, borderRadius: "16px", p: { xs: 2.5, sm: 3 }, mb: 3 }}>
          <Typography sx={{ fontFamily: THEME.hand, fontSize: "1.3rem", color: THEME.navyMid, position: "absolute", top: 14, right: 22, display: { xs: "none", sm: "block" } }}>
            ✦ Good Luck!
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
            <IconBadge bg="#DAD6F7" color={THEME.navyMid}>
              <DescriptionIcon fontSize="small" />
            </IconBadge>
            <Box>
              <Typography sx={{ fontFamily: THEME.display, fontWeight: 700, fontSize: "1.05rem", color: THEME.ink }}>
                Previous Academic Record
              </Typography>
              <Typography variant="body2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans, maxWidth: 480 }}>
                If you already have a CGPA from earlier semesters, enter it here so the overall CGPA
                below folds it in. Leave blank if you're starting fresh.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StepperField
                label="Previous CGPA"
                value={previousCgpa}
                onChange={setPreviousCgpa}
                step={0.01}
                min={0}
                max={10}
                decimals={2}
                icon={<StarIcon sx={{ fontSize: 16, color: THEME.gold }} />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StepperField
                label="Previous Total Credits"
                value={previousCredit}
                onChange={setPreviousCredit}
                step={1}
                min={0}
                icon={<LayersIcon sx={{ fontSize: 16, color: THEME.blue }} />}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 2, overflowX: "auto", pb: 0.5 }}>
          <Tabs
            value={activeSemester}
            onChange={(e, val) => setActiveSemester(val)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{ minHeight: 0, "& .MuiTabs-flexContainer": { gap: 1 } }}
          >
            {Array.from({ length: totalSemesters }, (_, i) => i + 1).map((sem) => (
              <Tab
                key={sem}
                value={sem}
                label={semesterResults[sem] ? `Semester ${sem} (${semesterResults[sem].gpa})` : `Semester ${sem}`}
                sx={{
                  textTransform: "none",
                  fontFamily: THEME.sans,
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  minHeight: 0,
                  minWidth: "auto",
                  borderRadius: "999px",
                  px: 2.4,
                  py: 1,
                  color: THEME.navy,
                  backgroundColor: THEME.surface,
                  border: `1px solid ${THEME.line}`,
                  "&.Mui-selected": { color: "#FFFFFF", backgroundColor: THEME.navy, border: `1px solid ${THEME.navy}` },
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ backgroundColor: THEME.surface, borderRadius: "16px", border: `1px solid ${THEME.line}`, p: { xs: 2.5, sm: 3 }, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <IconBadge>
                <MenuBookIcon fontSize="small" />
              </IconBadge>
              <Box>
                <Typography sx={{ fontFamily: THEME.display, fontWeight: 700, fontSize: "1.1rem", color: THEME.ink }}>
                  Semester {activeSemester} Subjects
                </Typography>
                {activeSubjects.length === 0 && (
                  <Typography variant="body2" sx={{ color: THEME.textFaint, fontFamily: THEME.sans, fontStyle: "italic" }}>
                    No subjects added yet for Semester {activeSemester}. Click "Add Subject" to start — course code is optional.
                  </Typography>
                )}
              </Box>
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => addSubject(activeSemester)}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontFamily: THEME.sans,
                fontWeight: 700,
                borderRadius: "999px",
                whiteSpace: "nowrap",
                color: THEME.navy,
                borderColor: THEME.lineStrong,
                "&:hover": { borderColor: THEME.navy, backgroundColor: THEME.surfaceTint },
              }}
            >
              Add Subject
            </Button>
          </Box>

          {activeSubjects.length > 0 && (
            <TableContainer sx={{ border: `1px solid ${THEME.line}`, borderRadius: "12px", mb: 2, overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: THEME.surfaceTint }}>
                    <TableCell sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>S.No</TableCell>
                    <TableCell sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>
                      Course code
                      <Typography component="span" sx={{ fontSize: "0.7rem", color: THEME.textFaint, ml: 0.5 }}>(optional)</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>Course title</TableCell>
                    <TableCell sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>Type</TableCell>
                    <TableCell align="center" sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>Credits</TableCell>
                    <TableCell align="center" sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>
                      Marks
                      <Typography component="span" sx={{ fontSize: "0.7rem", color: THEME.textFaint, ml: 0.5 }}>(optional)</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>Grade</TableCell>
                    <TableCell align="center" sx={{ fontFamily: THEME.sans, fontWeight: 700, color: THEME.textMuted, borderColor: THEME.line }}>GP</TableCell>
                    <TableCell align="center" sx={{ borderColor: THEME.line }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeSubjects.map((subject, index) => {
                    const gradeObj = GRADE_POINTS.find((g) => g.grade === subject.grade);
                    return (
                      <TableRow key={subject.id} sx={{ backgroundColor: index % 2 === 1 ? "rgba(21,42,74,0.02)" : "transparent" }}>
                        <TableCell sx={{ color: THEME.ink, borderColor: THEME.line, fontFamily: THEME.mono }}>{index + 1}</TableCell>
                        <TableCell sx={{ borderColor: THEME.line, minWidth: 110 }}>
                          <TextField
                            variant="standard"
                            value={subject.code}
                            placeholder="e.g. CP2501"
                            onChange={(e) => updateSubject(activeSemester, subject.id, "code", e.target.value)}
                            InputProps={{ disableUnderline: true }}
                            sx={{ "& input": { color: THEME.ink, fontFamily: THEME.mono, fontSize: "0.9rem" } }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderColor: THEME.line, minWidth: 160 }}>
                          <TextField
                            variant="standard"
                            value={subject.name}
                            placeholder="Subject name"
                            onChange={(e) => updateSubject(activeSemester, subject.id, "name", e.target.value)}
                            InputProps={{ disableUnderline: true }}
                            sx={{ "& input": { color: THEME.ink, fontFamily: THEME.sans, fontSize: "0.9rem" } }}
                          />
                        </TableCell>
                        <TableCell sx={{ borderColor: THEME.line, minWidth: 130 }}>
                          <TextField
                            select
                            variant="standard"
                            value={subject.type}
                            onChange={(e) => updateSubject(activeSemester, subject.id, "type", e.target.value)}
                            InputProps={{ disableUnderline: true }}
                            sx={{ "& .MuiSelect-select": { color: THEME.ink, fontFamily: THEME.sans, fontSize: "0.85rem" } }}
                          >
                            {SUBJECT_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>{t}</MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell align="center" sx={{ borderColor: THEME.line, width: 80 }}>
                          <TextField
                            variant="standard"
                            type="number"
                            value={subject.credit}
                            onChange={(e) => updateSubject(activeSemester, subject.id, "credit", e.target.value)}
                            InputProps={{ disableUnderline: true, inputProps: { min: 0, step: 0.5, style: { textAlign: "center" } } }}
                            sx={{ "& input": { color: THEME.ink, fontFamily: THEME.mono, fontSize: "0.9rem" } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ borderColor: THEME.line, width: 90 }}>
                          <TextField
                            variant="standard"
                            type="number"
                            value={subject.marks}
                            placeholder="—"
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                                updateSubject(activeSemester, subject.id, "marks", val);
                              }
                            }}
                            InputProps={{ disableUnderline: true, inputProps: { min: 0, max: 100, style: { textAlign: "center" } } }}
                            sx={{ "& input": { color: THEME.ink, fontFamily: THEME.mono, fontSize: "0.9rem" } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ borderColor: THEME.line, width: 100 }}>
                          <TextField
                            select
                            variant="standard"
                            value={subject.grade}
                            onChange={(e) => updateSubject(activeSemester, subject.id, "grade", e.target.value)}
                            InputProps={{ disableUnderline: true }}
                            sx={{ "& .MuiSelect-select": { color: gradeColor(subject.grade), fontFamily: THEME.mono, fontWeight: 700, fontSize: "0.9rem" } }}
                          >
                            {GRADE_POINTS.map((g) => (
                              <MenuItem key={g.grade} value={g.grade}>{g.grade}</MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell align="center" sx={{ color: THEME.gold, borderColor: THEME.line, fontFamily: THEME.mono, fontWeight: 700 }}>
                          {gradeObj ? gradeObj.point : "—"}
                        </TableCell>
                        <TableCell align="center" sx={{ borderColor: THEME.line }}>
                          <IconButton size="small" onClick={() => removeSubject(activeSemester, subject.id)} sx={{ color: THEME.coral, "&:hover": { backgroundColor: "rgba(217,100,85,0.12)" } }}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {error && (
            <Typography sx={{ color: THEME.coral, fontFamily: THEME.sans, fontSize: "0.875rem", mb: 2 }}>{error}</Typography>
          )}

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1, mb: activeResult ? 2 : 0 }}>
            <Button
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={() => calculateSemesterGPA(activeSemester)}
              disableElevation
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontFamily: THEME.sans,
                borderRadius: "999px",
                px: 3.5,
                py: 1,
                color: "#FFFFFF",
                backgroundColor: THEME.navy,
                "&:hover": { backgroundColor: THEME.navyDeep },
              }}
            >
              Calculate GPA
            </Button>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={resetAll}
              sx={{
                textTransform: "none",
                fontFamily: THEME.sans,
                fontWeight: 700,
                borderRadius: "999px",
                px: 3,
                color: THEME.textMuted,
                borderColor: THEME.line,
                "&:hover": { borderColor: THEME.lineStrong, backgroundColor: THEME.surfaceTint, color: THEME.navy },
              }}
            >
              Reset All
            </Button>
          </Box>

          {activeResult && (
            <Box sx={{ p: 3, backgroundColor: THEME.surfaceTint, borderRadius: "14px", borderLeft: `4px solid ${THEME.blue}` }}>
              <Typography sx={{ fontFamily: THEME.sans, fontWeight: 700, fontSize: "0.85rem", color: THEME.blueDeep, mb: 1.5 }}>
                Semester {activeSemester} — this term only
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>GPA</Typography>
                    <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2rem", color: THEME.blueDeep, my: 0.5 }}>{activeResult.gpa}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Percentage</Typography>
                    <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2rem", color: THEME.ink, my: 0.5 }}>{activeResult.pct}%</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Credits</Typography>
                    <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "1.4rem", color: THEME.ink, my: 0.5 }}>{activeResult.credits}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Status</Typography>
                    <Chip
                      label={getResultLabel(activeResult.gpa)}
                      sx={{ mt: 0.5, backgroundColor: "rgba(47,111,224,0.14)", color: THEME.blueDeep, border: "1px solid rgba(47,111,224,0.35)", fontFamily: THEME.sans, fontWeight: 700, borderRadius: "8px" }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        <Box sx={{ textAlign: "center", backgroundColor: THEME.mint, borderRadius: "18px", p: { xs: 3, sm: 4 }, mb: 4 }}>
          <Typography sx={{ fontFamily: THEME.display, fontWeight: 700, fontSize: "1.2rem", color: THEME.ink, mb: 0.5 }}>
            🏆 Final CGPA &amp; Percentage
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans, mb: 2.5 }}>
            Calculated across every semester you've entered subjects for.
          </Typography>
          <Button
            variant="contained"
            startIcon={<TrendingUpIcon />}
            onClick={calculateFinalCgpa}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontFamily: THEME.sans,
              borderRadius: "999px",
              px: 4,
              py: 1.2,
              color: "#FFFFFF",
              background: `linear-gradient(135deg, ${THEME.navyMid}, ${THEME.navy})`,
              "&:hover": { background: THEME.navyDeep },
            }}
          >
            Generate Final CGPA
          </Button>

          {finalResult && (
            <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2.5, textAlign: "left" }}>
              {finalResult.previousCgpa !== null && (
                <Box sx={{ p: 3, backgroundColor: THEME.surface, borderRadius: "14px", borderLeft: `4px solid ${THEME.textFaint}` }}>
                  <Typography sx={{ fontFamily: THEME.sans, fontWeight: 700, fontSize: "0.85rem", color: THEME.textMuted, mb: 1.5 }}>
                    Previous record — {finalResult.previousCredit} credits
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Previous CGPA</Typography>
                        <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2rem", color: THEME.textMuted, my: 0.5 }}>{finalResult.previousCgpa}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Percentage</Typography>
                        <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2rem", color: THEME.ink, my: 0.5 }}>{finalResult.previousPct}%</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {finalResult.semesterGpa !== null && (
                <Box sx={{ p: 3, backgroundColor: THEME.surface, borderRadius: "14px", borderLeft: `4px solid ${THEME.blue}` }}>
                  <Typography sx={{ fontFamily: THEME.sans, fontWeight: 700, fontSize: "0.85rem", color: THEME.blueDeep, mb: 1.5 }}>
                    Current entry — {finalResult.semestersCounted} semester(s), {finalResult.semesterCredits} credits
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>GPA</Typography>
                        <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2rem", color: THEME.blueDeep, my: 0.5 }}>{finalResult.semesterGpa}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Percentage</Typography>
                        <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2rem", color: THEME.ink, my: 0.5 }}>{finalResult.semesterPct}%</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Status</Typography>
                        <Chip
                          label={getResultLabel(finalResult.semesterGpa)}
                          sx={{ mt: 0.5, backgroundColor: "rgba(47,111,224,0.14)", color: THEME.blueDeep, border: "1px solid rgba(47,111,224,0.35)", fontFamily: THEME.sans, fontWeight: 700, borderRadius: "8px" }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ p: 3, backgroundColor: "rgba(240,169,60,0.12)", borderRadius: "14px", border: `1.5px solid ${THEME.lineStrong}`, borderLeft: `4px solid ${THEME.gold}` }}>
                <Typography sx={{ fontFamily: THEME.sans, fontWeight: 700, fontSize: "0.85rem", color: THEME.goldDeep, mb: 1.5, textAlign: "center" }}>
                  Overall — {finalResult.totalCredits} total credits
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Cumulative CGPA</Typography>
                      <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2.4rem", color: THEME.goldDeep, my: 0.5 }}>{finalResult.cgpa}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Percentage</Typography>
                      <Typography sx={{ fontFamily: THEME.mono, fontWeight: 700, fontSize: "2.4rem", color: THEME.ink, my: 0.5 }}>{finalResult.pct}%</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="subtitle2" sx={{ color: THEME.textMuted, fontFamily: THEME.sans }}>Standing</Typography>
                      <Chip
                        label={getResultLabel(finalResult.cgpa)}
                        sx={{ mt: 0.5, backgroundColor: "rgba(240,169,60,0.18)", color: THEME.goldDeep, border: `1px solid ${THEME.lineStrong}`, fontFamily: THEME.sans, fontWeight: 700, borderRadius: "8px" }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </Box>

        <Box component="section" sx={{ p: { xs: 2.5, sm: 3.5, md: 4 }, borderRadius: "16px", backgroundColor: THEME.surface, border: `1.5px solid ${THEME.line}` }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
            <IconBadge>
              <EmojiObjectsIcon fontSize="small" />
            </IconBadge>
            <Box>
              <Typography component="h2" sx={{ fontFamily: THEME.display, fontWeight: 700, fontSize: "1.3rem", color: THEME.ink, mb: 1 }}>
                Free Online CGPA Calculator for Engineering &amp; Arts Students
              </Typography>
              <Typography sx={{ color: THEME.textMuted, fontFamily: THEME.sans, lineHeight: 1.8 }}>
                This CGPA calculator helps BE, B.Tech, ME, M.Tech, and Arts &amp; Science students
                calculate their semester GPA and cumulative CGPA in seconds. It follows the Anna
                University 10-point grading system and also works for most autonomous colleges that
                use the same O, A+, A, B+, B, C grading scale.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2, mt: 3 }}>
            <IconBadge>
              <MenuBookIcon fontSize="small" />
            </IconBadge>
            <Box>
              <Typography component="h3" sx={{ fontFamily: THEME.display, fontWeight: 700, fontSize: "1.05rem", color: THEME.ink, mb: 1 }}>
                How to Calculate CGPA
              </Typography>
              <Typography sx={{ color: THEME.textMuted, fontFamily: THEME.sans, lineHeight: 1.8 }}>
                CGPA is calculated by multiplying each subject's credit value by its grade point,
                summing that across every subject you've studied, and dividing the total by the sum
                of all credits. Pick your degree and department once from the popup, then fill in
                each semester's subjects — course code is optional, everything else drives the
                calculation.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2, mt: 3 }}>
            <IconBadge>
              <DescriptionIcon fontSize="small" />
            </IconBadge>
            <Box>
              <Typography component="h3" sx={{ fontFamily: THEME.display, fontWeight: 700, fontSize: "1.05rem", color: THEME.ink, mb: 1 }}>
                CGPA to Percentage Conversion Formula
              </Typography>
              <Typography sx={{ color: THEME.textMuted, fontFamily: THEME.sans, lineHeight: 1.8 }}>
                For Anna University and most colleges on the 10-point scale, the standard formula is:
                Percentage = (CGPA × 10) − 7.5. This calculator applies that formula automatically for
                both your semester result and your overall cumulative result.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.5, mt: 3 }}>
            <IconBadge>
              <HelpCenterIcon fontSize="small" />
            </IconBadge>
            <Typography component="h3" sx={{ fontFamily: THEME.display, fontWeight: 700, fontSize: "1.05rem", color: THEME.ink }}>
              Frequently Asked Questions
            </Typography>
          </Box>

          {[
            { q: "How is CGPA calculated?", a: "CGPA is calculated by multiplying each subject's credit by its grade point, adding these up across all subjects and semesters, then dividing by the total credits." },
            { q: "How do I convert CGPA to percentage?", a: "For Anna University and most autonomous colleges following the 10-point scale, Percentage = (CGPA × 10) − 7.5." },
            { q: "What is the difference between GPA and CGPA?", a: "GPA is your grade point average for a single semester, while CGPA is your cumulative average across all completed semesters." },
            { q: "Is this CGPA calculator free to use?", a: "Yes, this CGPA calculator is completely free, works online, and needs no sign-up or download." },
          ].map((item, i) => (
            <Accordion key={i} elevation={0} sx={{ backgroundColor: THEME.surfaceTint, borderRadius: "12px !important", border: `1px solid ${THEME.line}`, mb: 1.5, "&:before": { display: "none" } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: THEME.textMuted }} />}>
                <Typography sx={{ fontWeight: 700, color: THEME.navy, fontFamily: THEME.sans, fontSize: "0.95rem" }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: THEME.textMuted, fontFamily: THEME.sans, lineHeight: 1.7 }}>{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mt: 4 }}>
          <Typography sx={{ fontFamily: THEME.hand, fontSize: "1.6rem", color: THEME.navyMid, lineHeight: 1.15 }}>
            Better Grades
            <br />
            Brighter Future
          </Typography>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <PencilCupIllustration />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default CgpaPage;